import { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { useFetcher } from "react-router";
import { marked } from "marked";
import { Button } from "~/components/ui/button";
import { useT } from "~/i18n/context";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "~/components/ui/dialog";

function downloadUrl(url: string, filename: string) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function CopyButton({ getText }: { getText: () => string }) {
  const { t } = useT();
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(getText()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [getText]);

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="h-6 w-6 flex items-center justify-center rounded text-muted-foreground/50 hover:text-foreground hover:bg-muted transition-colors"
      aria-label={t.caraChat.copyMessage}
    >
      {copied ? (
        <svg viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5 text-emerald-500">
          <path fillRule="evenodd" d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z" clipRule="evenodd" />
        </svg>
      ) : (
        <svg viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5">
          <path d="M10.5 1h-7A1.5 1.5 0 0 0 2 2.5v9a.5.5 0 0 0 1 0v-9a.5.5 0 0 1 .5-.5h7a.5.5 0 0 0 0-1Z" />
          <path d="M12.5 3h-7A1.5 1.5 0 0 0 4 4.5v9A1.5 1.5 0 0 0 5.5 15h7a1.5 1.5 0 0 0 1.5-1.5v-9A1.5 1.5 0 0 0 12.5 3Zm.5 10.5a.5.5 0 0 1-.5.5h-7a.5.5 0 0 1-.5-.5v-9a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 .5.5v9Z" />
        </svg>
      )}
    </button>
  );
}

function DownloadButton({ dataUrl, label }: { dataUrl: string; label: string }) {
  const { t } = useT();
  return (
    <button
      type="button"
      onClick={() => downloadUrl(dataUrl, `cara-${label.toLowerCase().replace(/\s+/g, "-")}.png`)}
      className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
      aria-label={`${t.caraChat.download} ${label}`}
    >
      <svg viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5">
        <path d="M2.75 14A1.75 1.75 0 0 1 1 12.25v-2.5a.75.75 0 0 1 1.5 0v2.5c0 .138.112.25.25.25h10.5a.25.25 0 0 0 .25-.25v-2.5a.75.75 0 0 1 1.5 0v2.5A1.75 1.75 0 0 1 13.25 14H2.75Z" />
        <path d="M7.25 7.689V2a.75.75 0 0 1 1.5 0v5.689l1.97-1.969a.749.749 0 1 1 1.06 1.06l-3.25 3.25a.749.749 0 0 1-1.06 0L4.22 6.78a.749.749 0 1 1 1.06-1.06l1.97 1.969Z" />
      </svg>
      {t.caraChat.download}
    </button>
  );
}

function formatRelativeTime(date: Date | string | undefined, labels: { justNow: string; minutesAgo: string }, locale?: string): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return labels.justNow;
  if (diffMin < 60) return labels.minutesAgo.replace("{n}", String(diffMin));
  if (diffMin < 1440) return d.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" });
  return d.toLocaleDateString(locale, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

type ToolResult = {
  status: string;
  phase?: string;
  dataUrl?: string;
  videoBase64?: string;
  videoUrl?: string;
  mediaType?: string;
  findings?: Array<{
    severity: "pass" | "warning" | "critical";
    area: string;
    finding: string;
    recommendation: string;
  }>;
  bom?: {
    metal: { type: string; weightGrams: number; costEstimate: number };
    stones: Array<{ type: string; cut: string; count: number; subtotal: number }>;
    labor: number;
    totalEstimate: number;
  };
  analysis?: {
    stone: string;
    setting: string;
    metal: string;
    compatible: boolean;
    concerns: string[];
    recommendations: string[];
  };
  adjustments?: Array<{ parameter: string; from: string; to: string; rationale: string }>;
  error?: string;
  reason?: string;
  [key: string]: unknown;
};

function ToolSectionHeader({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 mb-2.5">
      <div className="h-px flex-1 bg-border/40 max-w-6" />
      <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70 font-medium">{label}</p>
      <div className="h-px flex-1 bg-border/40" />
    </div>
  );
}

function MoodBoardCard({ result }: { result: ToolResult }) {
  const { t } = useT();
  const tc = t.caraChat.toolCards;
  if (!result.dataUrl) return null;
  return (
    <div className="mt-4">
      <ToolSectionHeader label={tc.moodBoard} />
      <Dialog>
        <DialogTrigger asChild>
          <button className="rounded-xl overflow-hidden border border-border hover:scale-[1.02] transition-transform duration-200 cursor-zoom-in">
            <img src={result.dataUrl} alt={tc.moodBoard} className="w-full max-w-xs sm:max-w-sm" loading="lazy" />
          </button>
        </DialogTrigger>
        <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 overflow-hidden">
          <img src={result.dataUrl} alt={tc.moodBoard} className="w-full h-full object-contain" />
        </DialogContent>
      </Dialog>
      <div className="mt-1.5">
        <DownloadButton dataUrl={result.dataUrl} label={tc.moodBoard} />
      </div>
    </div>
  );
}

function RenderCard({
  result,
  label,
  sessionId,
  sketchDataUrls,
  mode = "designer",
}: {
  result: ToolResult;
  label: string;
  sessionId?: string | null;
  sketchDataUrls?: string[];
  mode?: "designer" | "buyer";
}) {
  const { t } = useT();
  const tc = t.caraChat.toolCards;
  const isBuyer = mode === "buyer";
  const saveAction = isBuyer ? "/resources/cara-submit-brief" : "/resources/cara-save-design";
  const fetcher = useFetcher<{ ok?: boolean; designId?: string; orderId?: string; studioUrl?: string; error?: string }>();
  const [showTitleInput, setShowTitleInput] = useState(false);
  const [saveTitle, setSaveTitle] = useState("");

  if (!result.dataUrl) return null;

  const isSaving = fetcher.state !== "idle";
  const saved = fetcher.data?.ok === true;
  const saveError = fetcher.data?.error;

  function handleSave() {
    if (!result.dataUrl) return;
    const title = saveTitle.trim() || (isBuyer ? "Untitled Design Brief" : "Untitled Cara Design");
    const dp = result.designParameters as Record<string, unknown> | undefined;
    const description = typeof dp?.exactVisualDescription === "string"
      ? dp.exactVisualDescription
      : typeof dp?.inspirationSummary === "string"
        ? dp.inspirationSummary
        : "";

    fetcher.submit(
      {
        imageBase64: result.dataUrl,
        title,
        description,
        sessionId: sessionId ?? "",
        designParameters: dp ? JSON.stringify(dp) : "{}",
        sketchDataUrls: sketchDataUrls ?? [],
      },
      {
        method: "POST",
        action: saveAction,
        encType: "application/json",
      },
    );
    setShowTitleInput(false);
  }

  return (
    <div className="mt-4">
      <ToolSectionHeader label={label} />
      {result.adjustments && (
        <div className="mb-2 space-y-1">
          {result.adjustments.map((a, i) => (
            <p key={i} className="text-xs text-muted-foreground">
              <span className="text-foreground font-medium">{a.parameter}:</span> {a.from} → {a.to}
            </p>
          ))}
        </div>
      )}
      <Dialog>
        <DialogTrigger asChild>
          <button className="rounded-xl overflow-hidden border border-border hover:scale-[1.02] transition-transform duration-200 cursor-zoom-in">
            <img src={result.dataUrl} alt={label} className="w-full max-w-xs sm:max-w-sm" loading="lazy" />
          </button>
        </DialogTrigger>
        <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 overflow-hidden">
          <img src={result.dataUrl} alt={label} className="w-full h-full object-contain" />
        </DialogContent>
      </Dialog>

      <div className="mt-2 flex flex-wrap items-center gap-2 sm:gap-3">
        {saved ? (
          <a
            href={isBuyer ? `/trade/orders` : (fetcher.data?.studioUrl ?? "#")}
            className="inline-flex items-center gap-1.5 text-xs text-emerald-600 hover:text-emerald-500 font-medium transition-colors"
          >
            <svg viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5">
              <path fillRule="evenodd" d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z" clipRule="evenodd" />
            </svg>
            {isBuyer ? (tc.briefSubmitted ?? "Brief submitted — View Orders") : tc.savedOpenStudio}
          </a>
        ) : showTitleInput ? (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full">
            <input
              type="text"
              value={saveTitle}
              onChange={(e) => setSaveTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSave(); if (e.key === "Escape") setShowTitleInput(false); }}
              placeholder={tc.designTitlePlaceholder}
              className="flex-1 rounded-md border border-border bg-background px-2 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              autoFocus
            />
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="default"
                onClick={handleSave}
                disabled={isSaving}
                className="text-xs h-7 px-3"
              >
                {isSaving ? tc.saving : tc.save}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowTitleInput(false)}
                className="text-xs h-7 px-2"
              >
                {tc.cancel}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowTitleInput(true)}
              className="text-xs h-7 gap-1.5"
            >
              <svg viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5">
                <path d="M3.75 2a.75.75 0 0 0-.75.75v10.5c0 .414.336.75.75.75h8.5a.75.75 0 0 0 .75-.75V6.108a.75.75 0 0 0-.22-.53L9.172 2.22A.75.75 0 0 0 8.642 2H3.75Z" />
              </svg>
              {isBuyer ? (tc.submitBrief ?? "Submit Design Brief") : tc.saveToStudio}
            </Button>
            {saveError && (
              <span className="text-xs text-destructive">{saveError}</span>
            )}
          </div>
        )}
        {result.dataUrl && (
          <DownloadButton dataUrl={result.dataUrl} label={label} />
        )}
      </div>
    </div>
  );
}

function SeverityIcon({ severity }: { severity: "pass" | "warning" | "critical" }) {
  if (severity === "pass") {
    return (
      <svg viewBox="0 0 16 16" fill="currentColor" className="h-4 w-4 text-emerald-500">
        <path fillRule="evenodd" d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14Zm3.844-8.791a.75.75 0 0 0-1.188-.918l-3.7 4.79-1.649-1.833a.75.75 0 1 0-1.114 1.004l2.25 2.5a.75.75 0 0 0 1.15-.043l4.25-5.5Z" clipRule="evenodd" />
      </svg>
    );
  }
  if (severity === "warning") {
    return (
      <svg viewBox="0 0 16 16" fill="currentColor" className="h-4 w-4 text-amber-500">
        <path fillRule="evenodd" d="M6.701 2.25c.577-1 2.02-1 2.598 0l5.196 9a1.5 1.5 0 0 1-1.299 2.25H2.804a1.5 1.5 0 0 1-1.3-2.25l5.197-9ZM8 4a.75.75 0 0 1 .75.75v3a.75.75 0 0 1-1.5 0v-3A.75.75 0 0 1 8 4Zm0 8a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 16 16" fill="currentColor" className="h-4 w-4 text-red-500">
      <path fillRule="evenodd" d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14Zm2.78-4.22a.75.75 0 0 1-1.06 0L8 9.06l-1.72 1.72a.75.75 0 1 1-1.06-1.06L6.94 8 5.22 6.28a.75.75 0 0 1 1.06-1.06L8 6.94l1.72-1.72a.75.75 0 1 1 1.06 1.06L9.06 8l1.72 1.72a.75.75 0 0 1 0 1.06Z" clipRule="evenodd" />
    </svg>
  );
}

function StructuralAnalysisCard({ result }: { result: ToolResult }) {
  const { t } = useT();
  const tc = t.caraChat.toolCards;
  const ts = tc.structural;
  if (!result.findings) return null;

  const passCount = result.findings.filter((f) => f.severity === "pass").length;
  const warnCount = result.findings.filter((f) => f.severity === "warning").length;
  const critCount = result.findings.filter((f) => f.severity === "critical").length;

  const overallVerdict = critCount > 0 ? "critical" : warnCount > 0 ? "warning" : "pass";
  const verdictLabel = critCount > 0 ? ts.issuesFound : warnCount > 0 ? ts.warnings : ts.allClear;
  const verdictColor = critCount > 0
    ? "text-red-600 bg-red-500/10 border-red-500/30"
    : warnCount > 0
      ? "text-amber-600 bg-amber-500/10 border-amber-500/30"
      : "text-emerald-600 bg-emerald-500/10 border-emerald-500/30";

  const borderAccent = critCount > 0
    ? "border-red-500/20"
    : warnCount > 0
      ? "border-amber-500/20"
      : "border-emerald-500/20";

  return (
    <div className="mt-4">
      <ToolSectionHeader label={tc.structuralAnalysis} />
      <div className={`rounded-xl border ${borderAccent} bg-card overflow-hidden max-w-md`}>
        <div className="px-4 py-3.5 border-b border-border/50 bg-muted/30">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: "oklch(0.45 0.08 213 / 0.08)" }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" style={{ color: "var(--teal-sapphire)" }}>
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
                </svg>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70 font-medium leading-none mb-1">{ts.manufacturabilityReport}</p>
                <p className="text-xs text-foreground font-medium">{(result.findings.length !== 1 ? ts.areasAnalyzedPlural : ts.areasAnalyzed).replace("{n}", String(result.findings.length))}</p>
              </div>
            </div>
            <div className={`px-2.5 py-1 rounded-md border text-[10px] uppercase tracking-[0.15em] font-semibold ${verdictColor}`}>
              {verdictLabel}
            </div>
          </div>

          {/* Summary counters */}
          {(result.findings.length > 1 || overallVerdict !== "pass") && (
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border/30">
              {passCount > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  <span className="text-[11px] text-muted-foreground">{passCount} {ts.pass}</span>
                </div>
              )}
              {warnCount > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-amber-500" />
                  <span className="text-[11px] text-muted-foreground">{warnCount} {warnCount !== 1 ? ts.warningPlural : ts.warning}</span>
                </div>
              )}
              {critCount > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-red-500" />
                  <span className="text-[11px] text-muted-foreground">{critCount} {ts.critical}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Findings list */}
        <div className="divide-y divide-border/40">
          {result.findings.map((f, i) => {
            const isAllPass = overallVerdict === "pass" && result.findings!.length === 1;

            return (
              <div key={i} className="px-4 py-3 flex gap-3">
                <div className="shrink-0 mt-0.5">
                  <SeverityIcon severity={f.severity} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-xs font-semibold text-foreground">{f.area}</span>
                    <span className={`text-[9px] uppercase tracking-wider font-medium ${
                      f.severity === "pass" ? "text-emerald-600" : f.severity === "warning" ? "text-amber-600" : "text-red-600"
                    }`}>
                      {f.severity}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{f.finding}</p>
                  {f.recommendation && !isAllPass && (
                    <p className="text-xs text-foreground/70 mt-1.5 flex items-start gap-1.5">
                      <span className="shrink-0 text-muted-foreground/50 mt-px">→</span>
                      <span>{f.recommendation}</span>
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer recommendation when there are mixed results */}
        {overallVerdict !== "pass" && (
          <div className="px-4 py-3 border-t border-border/40 bg-muted/20">
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              <span className="font-medium text-foreground/80">{ts.recommendationLabel}</span>{" "}
              {critCount > 0
                ? ts.addressCritical
                : ts.reviewWarnings}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function BomCard({ result }: { result: ToolResult }) {
  const { t } = useT();
  const tc = t.caraChat.toolCards;
  const tb = tc.bom;
  if (!result.bom) return null;
  const b = result.bom;
  if (!b.metal) return null;
  const stoneLines = b.stones ?? [];
  const lineCount = 1 + stoneLines.length + 1;
  return (
    <div className="mt-4">
      <ToolSectionHeader label={tc.billOfMaterials} />
      <div className="rounded-xl border border-border bg-card overflow-hidden max-w-md">
        <div className="px-4 py-3.5 border-b border-border/50 bg-muted/30">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: "oklch(0.45 0.08 213 / 0.08)" }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" style={{ color: "var(--teal-sapphire)" }}>
                <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
                <rect x="9" y="3" width="6" height="4" rx="1" />
              </svg>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70 font-medium leading-none mb-1">{tb.costBreakdown}</p>
              <p className="text-xs text-foreground font-medium">{(lineCount !== 1 ? tb.lineItemsPlural : tb.lineItems).replace("{n}", String(lineCount))}</p>
            </div>
          </div>
        </div>
        <div className="px-4 py-3">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-muted-foreground/70">
                <th className="text-left pb-2 font-medium text-[10px] uppercase tracking-[0.15em]">{tb.item}</th>
                <th className="text-right pb-2 font-medium text-[10px] uppercase tracking-[0.15em]">{tb.qty}</th>
                <th className="text-right pb-2 font-medium text-[10px] uppercase tracking-[0.15em]">{tb.estCost}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/30">
              <tr>
                <td className="py-2 text-foreground">{b.metal.type}</td>
                <td className="py-2 text-right text-muted-foreground tabular-nums">{b.metal.weightGrams}g</td>
                <td className="py-2 text-right text-foreground tabular-nums">${b.metal.costEstimate.toLocaleString()}</td>
              </tr>
              {stoneLines.map((s, i) => (
                <tr key={i}>
                  <td className="py-2 text-foreground">{s.cut} {s.type}</td>
                  <td className="py-2 text-right text-muted-foreground tabular-nums">×{s.count}</td>
                  <td className="py-2 text-right text-foreground tabular-nums">${s.subtotal.toLocaleString()}</td>
                </tr>
              ))}
              <tr>
                <td className="py-2 text-foreground">{tb.laborSetting}</td>
                <td className="py-2 text-right text-muted-foreground">—</td>
                <td className="py-2 text-right text-foreground tabular-nums">${b.labor.toLocaleString()}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-border/40 bg-muted/20 flex items-center justify-between">
          <span className="text-xs font-medium text-foreground">{tb.totalEstimate}</span>
          <span className="text-sm font-semibold text-foreground tabular-nums">${b.totalEstimate.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
}

function MaterialRecCard({ result }: { result: ToolResult }) {
  const { t } = useT();
  const tc = t.caraChat.toolCards;
  const tm = tc.material;
  if (!result.analysis) return null;
  const a = result.analysis;
  const isOk = !!a.compatible;
  const borderAccent = isOk ? "border-emerald-500/20" : "border-amber-500/20";
  const verdictLabel = isOk ? tm.compatible : tm.concerns;
  const verdictColor = isOk
    ? "text-emerald-600 bg-emerald-500/10 border-emerald-500/30"
    : "text-amber-600 bg-amber-500/10 border-amber-500/30";

  return (
    <div className="mt-4">
      <ToolSectionHeader label={tc.materialAnalysis} />
      <div className={`rounded-xl border ${borderAccent} bg-card overflow-hidden max-w-md`}>
        <div className="px-4 py-3.5 border-b border-border/50 bg-muted/30">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: "oklch(0.45 0.08 213 / 0.08)" }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" style={{ color: "var(--teal-sapphire)" }}>
                  <path d="M2.7 10.3a2.41 2.41 0 0 0 0 3.41l7.59 7.59a2.41 2.41 0 0 0 3.41 0l7.59-7.59a2.41 2.41 0 0 0 0-3.41L13.7 2.71a2.41 2.41 0 0 0-3.41 0Z" />
                  <path d="M8 12h8" />
                </svg>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70 font-medium leading-none mb-1">{tm.compatibilityCheck}</p>
                <p className="text-xs text-foreground font-medium">{a.stone} · {a.setting} · {a.metal}</p>
              </div>
            </div>
            <div className={`px-2.5 py-1 rounded-md border text-[10px] uppercase tracking-[0.15em] font-semibold ${verdictColor}`}>
              {verdictLabel}
            </div>
          </div>
        </div>

        {(a.concerns ?? []).length > 0 && (
          <div className="px-4 py-3 border-b border-border/30">
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/60 font-medium mb-2">{tm.concernsLabel}</p>
            <div className="space-y-1.5">
              {(a.concerns ?? []).map((c, i) => (
                <div key={i} className="flex items-start gap-2 text-xs">
                  <svg viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5">
                    <path fillRule="evenodd" d="M6.701 2.25c.577-1 2.02-1 2.598 0l5.196 9a1.5 1.5 0 0 1-1.299 2.25H2.804a1.5 1.5 0 0 1-1.3-2.25l5.197-9ZM8 4a.75.75 0 0 1 .75.75v3a.75.75 0 0 1-1.5 0v-3A.75.75 0 0 1 8 4Zm0 8a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd" />
                  </svg>
                  <span className="text-muted-foreground">{c}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {(a.recommendations ?? []).length > 0 && (
          <div className="px-4 py-3">
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/60 font-medium mb-2">{tm.recommendationsLabel}</p>
            <div className="space-y-1.5">
              {(a.recommendations ?? []).map((r, i) => (
                <p key={i} className="text-xs text-foreground/80 flex items-start gap-1.5">
                  <span className="shrink-0 text-muted-foreground/50 mt-px">→</span>
                  <span>{r}</span>
                </p>
              ))}
            </div>
          </div>
        )}

        {isOk && (a.concerns ?? []).length === 0 && (
          <div className="px-4 py-3 flex items-center gap-2">
            <svg viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5 text-emerald-500 shrink-0">
              <path fillRule="evenodd" d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14Zm3.844-8.791a.75.75 0 0 0-1.188-.918l-3.7 4.79-1.649-1.833a.75.75 0 1 0-1.114 1.004l2.25 2.5a.75.75 0 0 0 1.15-.043l4.25-5.5Z" clipRule="evenodd" />
            </svg>
            <p className="text-[11px] text-muted-foreground">{tm.allCompatible}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function VideoCard({ result }: { result: ToolResult }) {
  const { t } = useT();
  const tc = t.caraChat.toolCards;
  const src = result.videoUrl
    ?? (result.videoBase64 ? `data:${result.mediaType || "video/mp4"};base64,${result.videoBase64}` : null);
  if (!src) return null;
  return (
    <div className="mt-4">
      <ToolSectionHeader label={tc.turntablePreview} />
      <div className="rounded-xl overflow-hidden border border-border bg-black/5">
        <video src={src} autoPlay loop muted playsInline className="w-full max-w-xs sm:max-w-sm" />
      </div>
      <div className="mt-1.5">
        <button
          type="button"
          onClick={() => {
            const a = document.createElement("a");
            a.href = src;
            a.download = "cara-turntable.mp4";
            a.click();
          }}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          aria-label={`${t.caraChat.download} ${tc.turntablePreview}`}
        >
          <svg viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5">
            <path d="M2.75 14A1.75 1.75 0 0 1 1 12.25v-2.5a.75.75 0 0 1 1.5 0v2.5c0 .138.112.25.25.25h10.5a.25.25 0 0 0 .25-.25v-2.5a.75.75 0 0 1 1.5 0v2.5A1.75 1.75 0 0 1 13.25 14H2.75Z" />
            <path d="M7.25 7.689V2a.75.75 0 0 1 1.5 0v5.689l1.97-1.969a.749.749 0 1 1 1.06 1.06l-3.25 3.25a.749.749 0 0 1-1.06 0L4.22 6.78a.749.749 0 1 1 1.06-1.06l1.97 1.969Z" />
          </svg>
          {t.caraChat.download}
        </button>
      </div>
    </div>
  );
}

function DesignSpecCard({ result }: { result: ToolResult }) {
  const { t } = useT();
  const td = t.caraChat.toolCards.designSpecs;
  const title = typeof result.title === "string" ? result.title : t.caraChat.toolLabels.present_design_specs;
  const jewelryType = typeof result.jewelryType === "string" ? result.jewelryType : null;
  const metal = typeof result.metal === "string" ? result.metal : null;
  const style = typeof result.style === "string" ? result.style : null;
  const stones = Array.isArray(result.stones) ? result.stones as Array<{ type: string; cut: string; count: number; setting: string; sizeRange?: string }> : [];
  const dimensions = result.dimensions as { widthMm?: number; heightMm?: number; depthMm?: number; shankWidthMm?: number; bandWidthMm?: number; wallThicknessMm?: number } | undefined;
  const finishes = Array.isArray(result.finishes) ? result.finishes as string[] : [];
  const weightGrams = typeof result.estimatedWeightGrams === "number" ? result.estimatedWeightGrams : null;
  const manufacturable = typeof result.manufacturable === "boolean" ? result.manufacturable : null;
  const mfgNote = typeof result.manufacturabilityNote === "string" ? result.manufacturabilityNote : null;
  const highlights = Array.isArray(result.highlights) ? result.highlights as string[] : [];

  const totalStones = stones.reduce((sum, s) => sum + s.count, 0);
  const hasDimensions = dimensions && Object.values(dimensions).some((v) => v != null);

  return (
    <div className="mt-4">
      <ToolSectionHeader label={title} />
      <div className="rounded-xl border border-border bg-card overflow-hidden max-w-md">
        {/* Header */}
        <div className="px-4 py-3.5 border-b border-border/50 bg-muted/30">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-primary/10 shrink-0">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-primary">
                <path d="M12 2 2 7l10 5 10-5-10-5Z" />
                <path d="m2 17 10 5 10-5" />
                <path d="m2 12 10 5 10-5" />
              </svg>
            </div>
            <div className="min-w-0">
              {jewelryType && style && (
                <p className="text-xs text-foreground font-medium capitalize">
                  {style} {jewelryType}
                </p>
              )}
              {metal && (
                <p className="text-[11px] text-muted-foreground">{metal}</p>
              )}
            </div>
          </div>
        </div>

        {/* Stat pills */}
        <div className="px-4 py-3 flex flex-wrap gap-2">
          {weightGrams != null && (
            <div className="rounded-lg bg-muted/40 px-3 py-2 text-center min-w-[4.5rem]">
              <p className="text-sm font-semibold text-foreground tabular-nums">~{weightGrams}g</p>
              <p className="text-[10px] text-muted-foreground/70 mt-0.5">{td.weight}</p>
            </div>
          )}
          {totalStones > 0 && (
            <div className="rounded-lg bg-muted/40 px-3 py-2 text-center min-w-[4.5rem]">
              <p className="text-sm font-semibold text-foreground tabular-nums">{totalStones}</p>
              <p className="text-[10px] text-muted-foreground/70 mt-0.5">{totalStones === 1 ? td.stoneSingular : td.stones}</p>
            </div>
          )}
          {finishes.length > 0 && (
            <div className="rounded-lg bg-muted/40 px-3 py-2 text-center min-w-[4.5rem]">
              <p className="text-sm font-semibold text-foreground capitalize">{finishes[0]}</p>
              <p className="text-[10px] text-muted-foreground/70 mt-0.5">{td.finish}</p>
            </div>
          )}
        </div>

        {/* Stones detail */}
        {stones.length > 0 && (
          <div className="px-4 pb-3">
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/60 font-medium mb-1.5">{td.stonesLabel}</p>
            <div className="space-y-1">
              {stones.map((s, i) => (
                <div key={i} className="flex items-baseline justify-between gap-2 text-xs">
                  <span className="text-foreground">
                    {s.count}× {s.cut} {s.type}
                    {s.sizeRange ? <span className="text-muted-foreground ml-1">({s.sizeRange})</span> : null}
                  </span>
                  <span className="text-muted-foreground shrink-0">{s.setting}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Dimensions */}
        {hasDimensions && (
          <div className="px-4 pb-3">
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/60 font-medium mb-1.5">{td.dimensions}</p>
            <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
              {dimensions!.shankWidthMm != null && <span>Shank {dimensions!.shankWidthMm}mm</span>}
              {dimensions!.bandWidthMm != null && <span>Band {dimensions!.bandWidthMm}mm</span>}
              {dimensions!.widthMm != null && <span>W {dimensions!.widthMm}mm</span>}
              {dimensions!.heightMm != null && <span>H {dimensions!.heightMm}mm</span>}
              {dimensions!.depthMm != null && <span>D {dimensions!.depthMm}mm</span>}
              {dimensions!.wallThicknessMm != null && <span>Walls {dimensions!.wallThicknessMm}mm</span>}
            </div>
          </div>
        )}

        {/* Highlights */}
        {highlights.length > 0 && (
          <div className="px-4 pb-3 space-y-1">
            {highlights.map((h, i) => (
              <p key={i} className="text-xs text-foreground/70 flex items-start gap-1.5">
                <span className="shrink-0 text-primary/60 mt-px">→</span>
                <span>{h}</span>
              </p>
            ))}
          </div>
        )}

        {/* Manufacturability footer */}
        {manufacturable != null && (
          <div className={`px-4 py-2.5 border-t border-border/40 flex items-center gap-2 ${
            manufacturable ? "bg-emerald-500/[0.04]" : "bg-amber-500/[0.04]"
          }`}>
            {manufacturable ? (
              <svg viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5 text-emerald-500 shrink-0">
                <path fillRule="evenodd" d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14Zm3.844-8.791a.75.75 0 0 0-1.188-.918l-3.7 4.79-1.649-1.833a.75.75 0 1 0-1.114 1.004l2.25 2.5a.75.75 0 0 0 1.15-.043l4.25-5.5Z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5 text-amber-500 shrink-0">
                <path fillRule="evenodd" d="M6.701 2.25c.577-1 2.02-1 2.598 0l5.196 9a1.5 1.5 0 0 1-1.299 2.25H2.804a1.5 1.5 0 0 1-1.3-2.25l5.197-9ZM8 4a.75.75 0 0 1 .75.75v3a.75.75 0 0 1-1.5 0v-3A.75.75 0 0 1 8 4Zm0 8a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd" />
              </svg>
            )}
            <p className="text-[11px] text-muted-foreground">
              {manufacturable ? td.manufacturable : td.manufacturabilityConcerns}
              {mfgNote ? ` — ${mfgNote}` : ""}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function FinalizeDesignCard({ result }: { result: ToolResult }) {
  const { t } = useT();
  const tc = t.caraChat.toolCards;
  const tf = tc.finalize;
  const title = typeof result.title === "string" ? result.title : null;
  const description = typeof result.description === "string" ? result.description : null;
  const weight = typeof result.totalMetalWeightGrams === "number" ? result.totalMetalWeightGrams : null;
  const stoneCount = typeof result.totalStoneCount === "number" ? result.totalStoneCount : null;
  const price = typeof result.estimatedRetailPriceUsd === "number" ? result.estimatedRetailPriceUsd : null;

  return (
    <div className="mt-4">
      <ToolSectionHeader label={tc.designFinalized} />
      <div className="rounded-xl border border-emerald-500/25 bg-card overflow-hidden max-w-md">
        {/* Header band */}
        <div className="px-4 py-3 bg-emerald-500/[0.06] border-b border-emerald-500/15 flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
            <svg viewBox="0 0 16 16" fill="currentColor" className="h-4 w-4 text-emerald-600">
              <path fillRule="evenodd" d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14Zm3.844-8.791a.75.75 0 0 0-1.188-.918l-3.7 4.79-1.649-1.833a.75.75 0 1 0-1.114 1.004l2.25 2.5a.75.75 0 0 0 1.15-.043l4.25-5.5Z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-600/80 font-medium leading-none mb-0.5">{tf.productionReady}</p>
            {title && (
              <p className="text-sm font-display font-medium text-foreground truncate">{title}</p>
            )}
          </div>
        </div>

        {/* Description */}
        {description && (
          <div className="px-4 pt-3 pb-2">
            <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
          </div>
        )}

        {/* Spec grid */}
        {(weight != null || stoneCount != null || price != null) && (
          <div className="px-4 py-3">
            <div className="grid grid-cols-3 gap-2">
              {weight != null && (
                <div className="rounded-lg bg-muted/40 px-3 py-2.5 text-center">
                  <p className="text-sm font-semibold text-foreground tabular-nums">{weight}g</p>
                  <p className="text-[10px] text-muted-foreground/70 mt-0.5">{tf.metal}</p>
                </div>
              )}
              {stoneCount != null && (
                <div className="rounded-lg bg-muted/40 px-3 py-2.5 text-center">
                  <p className="text-sm font-semibold text-foreground tabular-nums">{stoneCount}</p>
                  <p className="text-[10px] text-muted-foreground/70 mt-0.5">{stoneCount === 1 ? tf.stoneSingular : tf.stones}</p>
                </div>
              )}
              {price != null && (
                <div className="rounded-lg bg-muted/40 px-3 py-2.5 text-center">
                  <p className="text-sm font-semibold text-foreground tabular-nums">${price.toLocaleString()}</p>
                  <p className="text-[10px] text-muted-foreground/70 mt-0.5">{tf.estRetail}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-4 py-2.5 border-t border-border/40 bg-muted/20 flex items-center gap-2">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0">
            <path d="M14 2H2v12h12V2Z" />
            <path d="M6 2v12M2 6h12" />
          </svg>
          <p className="text-[11px] text-muted-foreground">
            {tf.cadHandoff}
          </p>
        </div>
      </div>
    </div>
  );
}

function GalleryShareCard({ result }: { result: ToolResult }) {
  const { t } = useT();
  const tc = t.caraChat.toolCards;
  const tg = tc.gallery;
  const title = typeof result.title === "string" ? result.title : "";

  return (
    <div className="mt-4">
      <ToolSectionHeader label={tc.sharedToGallery} />
      <div className="rounded-xl border border-violet-500/25 bg-card overflow-hidden max-w-md">
        <div className="px-4 py-3.5 bg-violet-500/[0.06] border-b border-violet-500/15 flex items-center gap-3">
          <div className="h-8 w-8 rounded-lg bg-violet-500/10 flex items-center justify-center shrink-0">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-violet-600">
              <rect width="7" height="7" x="3" y="3" rx="1" />
              <rect width="7" height="7" x="14" y="3" rx="1" />
              <rect width="7" height="7" x="3" y="14" rx="1" />
              <rect width="7" height="7" x="14" y="14" rx="1" />
            </svg>
          </div>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] text-violet-600/80 font-medium leading-none mb-0.5">{tg.nowInGallery}</p>
            <p className="text-sm font-display font-medium text-foreground truncate">{title}</p>
          </div>
        </div>
        <div className="px-4 py-3 flex items-center gap-2.5 text-xs text-muted-foreground">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5 text-violet-500/60 shrink-0">
            <path d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z" />
          </svg>
          <span>{tg.storyGenerating}</span>
        </div>
      </div>
    </div>
  );
}

function ErrorCard({ result }: { result: ToolResult }) {
  const { t } = useT();
  return (
    <div className="mt-4 rounded-xl border border-destructive/25 bg-card overflow-hidden max-w-md">
      <div className="px-4 py-3 bg-destructive/[0.06] flex items-start gap-2.5">
        <div className="h-6 w-6 rounded-md bg-destructive/10 flex items-center justify-center shrink-0 mt-0.5">
          <svg viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5 text-destructive">
            <path fillRule="evenodd" d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14Zm2.78-4.22a.75.75 0 0 1-1.06 0L8 9.06l-1.72 1.72a.75.75 0 1 1-1.06-1.06L6.94 8 5.22 6.28a.75.75 0 0 1 1.06-1.06L8 6.94l1.72-1.72a.75.75 0 1 1 1.06 1.06L9.06 8l1.72 1.72a.75.75 0 0 1 0 1.06Z" clipRule="evenodd" />
          </svg>
        </div>
        <p className="text-xs text-destructive/90 leading-relaxed pt-0.5">{result.error || result.reason || t.caraChat.toolCards.errorFallback}</p>
      </div>
    </div>
  );
}

function SketchAnalysisCard({ result }: { result: ToolResult }) {
  const { t } = useT();
  const tc = t.caraChat.toolCards;
  const ts = tc.sketch;
  const elements = result.visibleElements as {
    hasStones?: boolean;
    stoneCount?: number;
    hasFiligree?: boolean;
    hasEngraving?: boolean;
    symmetry?: string;
    silhouette?: string;
  } | undefined;
  const notes = result.designNotes as string[] | undefined;
  const clarifications = result.clarificationNeeded as string[] | undefined;
  const jewelryType = typeof result.inferredJewelryType === "string" ? result.inferredJewelryType : undefined;
  const style = typeof result.inferredStyle === "string" ? result.inferredStyle : undefined;
  const hasClarifications = clarifications && clarifications.length > 0;
  const borderAccent = hasClarifications ? "border-amber-500/20" : "border-border";

  return (
    <div className="mt-4">
      <ToolSectionHeader label={tc.sketchAnalysis} />
      <div className={`rounded-xl border ${borderAccent} bg-card overflow-hidden max-w-md`}>
        <div className="px-4 py-3.5 border-b border-border/50 bg-muted/30">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: "oklch(0.45 0.08 213 / 0.08)" }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" style={{ color: "var(--teal-sapphire)" }}>
                  <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70 font-medium leading-none mb-1">{ts.sketchInterpretation}</p>
                {(jewelryType || style) && (
                  <p className="text-xs text-foreground font-medium capitalize">
                    {[style, jewelryType].filter(Boolean).join(" ")}
                  </p>
                )}
              </div>
            </div>
            {hasClarifications && (
              <div className="px-2.5 py-1 rounded-md border text-[10px] uppercase tracking-[0.15em] font-semibold text-amber-600 bg-amber-500/10 border-amber-500/30">
                {ts.needsInput}
              </div>
            )}
          </div>
        </div>

        {/* Detected attributes */}
        {(elements || (jewelryType || style)) && (
          <div className="px-4 py-3 flex flex-wrap gap-2">
            {elements?.symmetry && elements.symmetry !== "unknown" && (
              <span className="rounded-md bg-muted/50 px-2 py-1 text-[10px] text-muted-foreground font-medium">{elements.symmetry}</span>
            )}
            {elements?.silhouette && (
              <span className="rounded-md bg-muted/50 px-2 py-1 text-[10px] text-muted-foreground font-medium">{elements.silhouette}</span>
            )}
            {elements?.hasStones && (
              <span className="rounded-md bg-muted/50 px-2 py-1 text-[10px] text-muted-foreground font-medium">
                {ts.stones}{elements.stoneCount ? `: ${elements.stoneCount}` : ""}
              </span>
            )}
            {elements?.hasFiligree && (
              <span className="rounded-md bg-muted/50 px-2 py-1 text-[10px] text-muted-foreground font-medium">{ts.filigree}</span>
            )}
            {elements?.hasEngraving && (
              <span className="rounded-md bg-muted/50 px-2 py-1 text-[10px] text-muted-foreground font-medium">{ts.engraving}</span>
            )}
          </div>
        )}

        {notes && notes.length > 0 && (
          <div className="px-4 pb-3">
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/60 font-medium mb-1.5">{ts.designNotes}</p>
            <div className="space-y-1">
              {notes.map((n, i) => (
                <p key={i} className="text-xs text-foreground/80 flex items-start gap-1.5">
                  <span className="shrink-0 text-muted-foreground/50 mt-px">→</span>
                  <span>{n}</span>
                </p>
              ))}
            </div>
          </div>
        )}

        {hasClarifications && (
          <div className="px-4 py-3 border-t border-border/40 bg-amber-500/[0.04]">
            <p className="text-[10px] uppercase tracking-[0.18em] text-amber-600/70 font-medium mb-1.5">{ts.needsClarification}</p>
            <div className="space-y-1">
              {clarifications!.map((c, i) => (
                <p key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                  <span className="shrink-0 text-amber-500/70 mt-px">?</span>
                  <span>{c}</span>
                </p>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ConceptSketchCard({ result }: { result: ToolResult }) {
  const { t } = useT();
  const tc = t.caraChat.toolCards;
  if (!result.dataUrl) return null;
  const isRefinement = result.isRefinement === true;
  const sketchStyle = typeof result.sketchStyle === "string" ? result.sketchStyle : "pencil";
  const views = typeof result.views === "string" ? result.views : "single";
  const headerLabel = isRefinement ? tc.refinedSketch : tc.conceptSketch;
  const tagParts = [sketchStyle, views === "multi-angle" ? "multi-view" : null].filter(Boolean);

  return (
    <div className="mt-4">
      <ToolSectionHeader label={headerLabel} />
      {tagParts.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {tagParts.map((t) => (
            <span key={t} className="rounded-md bg-muted/50 px-2 py-0.5 text-[10px] text-muted-foreground/70 font-medium capitalize">{t}</span>
          ))}
        </div>
      )}
      <Dialog>
        <DialogTrigger asChild>
          <button className="rounded-xl overflow-hidden border border-border bg-[oklch(0.97_0.005_80)] hover:scale-[1.02] transition-transform duration-200 cursor-zoom-in">
            <img src={result.dataUrl} alt={headerLabel} className="w-full max-w-xs sm:max-w-sm" loading="lazy" />
          </button>
        </DialogTrigger>
        <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 overflow-hidden">
          <img src={result.dataUrl} alt={`${headerLabel} — full size`} className="w-full h-full object-contain" />
        </DialogContent>
      </Dialog>
      <div className="mt-1.5">
        <DownloadButton dataUrl={result.dataUrl} label={headerLabel} />
      </div>
    </div>
  );
}

function InspirationCard({ result }: { result: ToolResult }) {
  const { t } = useT();
  const tc = t.caraChat.toolCards;
  const ti = tc.inspiration;
  const elements = result.extractedElements as {
    geometricMotifs?: string[];
    textureProfile?: string[];
    colorPalette?: string[];
    emotionalTone?: string;
  } | undefined;
  const motifs = elements?.geometricMotifs ?? [];
  const textures = elements?.textureProfile ?? [];
  const colors = elements?.colorPalette ?? [];
  const tone = elements?.emotionalTone;
  const finishes = (result.suggestedMetalFinishes ?? []) as string[];
  const forms = (result.suggestedJewelryForms ?? []) as string[];

  return (
    <div className="mt-4">
      <ToolSectionHeader label={tc.inspirationAnalysis} />
      <div className="rounded-xl border border-border bg-card overflow-hidden max-w-md">
        <div className="px-4 py-3.5 border-b border-border/50 bg-muted/30">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: "oklch(0.45 0.08 213 / 0.08)" }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" style={{ color: "var(--teal-sapphire)" }}>
                <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.582a.5.5 0 0 1 0 .963L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
              </svg>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70 font-medium leading-none mb-1">{ti.inspirationBreakdown}</p>
              {tone && (
                <p className="text-xs text-foreground font-medium capitalize">{tone}</p>
              )}
            </div>
          </div>
        </div>

        {colors.length > 0 && (
          <div className="px-4 py-3 border-b border-border/30">
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/60 font-medium mb-2">{ti.colorPalette}</p>
            <div className="flex flex-wrap gap-2">
              {colors.map((c, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 rounded-md bg-muted/50 px-2 py-1 text-[10px] text-muted-foreground font-medium capitalize">
                  <span className="h-2.5 w-2.5 rounded-full border border-border/50 shrink-0" style={{ background: colorNameToHint(c) }} />
                  {c}
                </span>
              ))}
            </div>
          </div>
        )}

        {(motifs.length > 0 || textures.length > 0) && (
          <div className="px-4 py-3 border-b border-border/30">
            {motifs.length > 0 && (
              <div className="mb-2">
                <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/60 font-medium mb-1.5">{ti.motifs}</p>
                <div className="flex flex-wrap gap-1.5">
                  {motifs.map((m, i) => (
                    <span key={i} className="rounded-md bg-muted/50 px-2 py-1 text-[10px] text-muted-foreground font-medium capitalize">{m}</span>
                  ))}
                </div>
              </div>
            )}
            {textures.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/60 font-medium mb-1.5">{ti.textures}</p>
                <div className="flex flex-wrap gap-1.5">
                  {textures.map((tx, i) => (
                    <span key={i} className="rounded-md bg-muted/50 px-2 py-1 text-[10px] text-muted-foreground font-medium capitalize">{tx}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {(finishes.length > 0 || forms.length > 0) && (
          <div className="px-4 py-3">
            {finishes.length > 0 && (
              <div className={forms.length > 0 ? "mb-3" : ""}>
                <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/60 font-medium mb-1.5">{ti.suggestedFinishes}</p>
                <div className="space-y-1">
                  {finishes.map((f, i) => (
                    <p key={i} className="text-xs text-foreground/80 flex items-start gap-1.5">
                      <span className="shrink-0 text-muted-foreground/50 mt-px">→</span>
                      <span className="capitalize">{f}</span>
                    </p>
                  ))}
                </div>
              </div>
            )}
            {forms.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/60 font-medium mb-1.5">{ti.suggestedForms}</p>
                <div className="space-y-1">
                  {forms.map((f, i) => (
                    <p key={i} className="text-xs text-foreground/80 flex items-start gap-1.5">
                      <span className="shrink-0 text-muted-foreground/50 mt-px">→</span>
                      <span className="capitalize">{f}</span>
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function colorNameToHint(name: string): string {
  const n = name.toLowerCase();
  if (n.includes("gold")) return "oklch(0.75 0.15 85)";
  if (n.includes("silver") || n.includes("cool")) return "oklch(0.78 0.01 250)";
  if (n.includes("rose")) return "oklch(0.72 0.12 15)";
  if (n.includes("blue") || n.includes("sapphire")) return "oklch(0.55 0.18 255)";
  if (n.includes("green") || n.includes("emerald") || n.includes("forest")) return "oklch(0.55 0.16 155)";
  if (n.includes("red") || n.includes("ruby")) return "oklch(0.55 0.2 25)";
  if (n.includes("purple") || n.includes("violet") || n.includes("amethyst")) return "oklch(0.55 0.17 300)";
  if (n.includes("pink")) return "oklch(0.72 0.14 350)";
  if (n.includes("black") || n.includes("dark") || n.includes("onyx")) return "oklch(0.25 0.01 0)";
  if (n.includes("white") || n.includes("ivory") || n.includes("cream")) return "oklch(0.95 0.02 90)";
  if (n.includes("warm")) return "oklch(0.75 0.1 65)";
  if (n.includes("orange") || n.includes("amber")) return "oklch(0.7 0.16 55)";
  if (n.includes("teal") || n.includes("turquoise") || n.includes("cyan")) return "oklch(0.6 0.12 195)";
  return "oklch(0.65 0.04 250)";
}

function ReferenceCard({ result }: { result: ToolResult }) {
  const { t } = useT();
  const tr = t.caraChat.toolCards.reference;
  const data = result.data as Record<string, unknown> | undefined;
  if (!data || Object.keys(data).length === 0) return null;

  const SECTION_LABELS: Record<string, string> = {
    casting: tr.casting,
    metals: tr.metals,
    stones: tr.stones,
    settingRules: tr.settings,
  };

  return (
    <div className="mt-4">
      <ToolSectionHeader label={tr.title} />
      <div className="rounded-xl border border-border bg-card overflow-hidden max-w-md">
        {Object.entries(data).map(([key, value]) => (
          <div key={key} className="px-4 py-3 border-b border-border/30 last:border-b-0">
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/60 font-medium mb-2">
              {SECTION_LABELS[key] ?? key}
            </p>
            <div className="space-y-1.5">
              {typeof value === "object" && value !== null ? (
                Object.entries(value as Record<string, unknown>).map(([subKey, subVal]) => (
                  <div key={subKey} className="flex items-start gap-2 text-xs">
                    <span className="text-muted-foreground/70 font-medium shrink-0 min-w-[80px] capitalize">
                      {subKey.replace(/_/g, " ")}
                    </span>
                    <span className="text-foreground/80">
                      {typeof subVal === "object" && subVal !== null
                        ? Object.entries(subVal as Record<string, string>).map(([k, v]) => `${k}: ${v}`).join(", ")
                        : String(subVal)}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-xs text-foreground/80">{String(value)}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ToolResultCard({
  toolName,
  result,
  sessionId,
  sketchDataUrls,
  mode = "designer",
}: {
  toolName: string;
  result: ToolResult;
  sessionId?: string | null;
  sketchDataUrls?: string[];
  mode?: "designer" | "buyer";
}) {
  const { t } = useT();
  const tc = t.caraChat.toolCards;
  if (result.status === "error") return <ErrorCard result={result} />;
  if (result.status === "skipped") return null;

  switch (toolName) {
    case "generate_mood_board":
      return <MoodBoardCard result={result} />;
    case "generate_concept_sketch":
      return <ConceptSketchCard result={result} />;
    case "generate_design_render":
      return <RenderCard result={result} label={tc.designRender} sessionId={sessionId} sketchDataUrls={sketchDataUrls} mode={mode} />;
    case "adjust_design":
      return <RenderCard result={result} label={tc.updatedRender} sessionId={sessionId} sketchDataUrls={sketchDataUrls} mode={mode} />;
    case "generate_catalog_render":
      return <RenderCard result={result} label={tc.catalogRender} sessionId={sessionId} sketchDataUrls={sketchDataUrls} mode={mode} />;
    case "render_from_sketch":
      return <RenderCard result={result} label={tc.conceptFromSketch} sessionId={sessionId} sketchDataUrls={sketchDataUrls} mode={mode} />;
    case "analyze_sketch":
      return <SketchAnalysisCard result={result} />;
    case "analyze_inspiration":
      return <InspirationCard result={result} />;
    case "reference_lookup":
      return <ReferenceCard result={result} />;
    case "structural_analysis":
      return <StructuralAnalysisCard result={result} />;
    case "calculate_bom":
      return <BomCard result={result} />;
    case "material_recommendation":
      return <MaterialRecCard result={result} />;
    case "generate_turntable_video":
      return <VideoCard result={result} />;
    case "generate_lookbook_image":
      return <RenderCard result={result} label={tc.lookbookLabel} sessionId={sessionId} sketchDataUrls={sketchDataUrls} mode={mode} />;
    case "present_design_specs":
      return <DesignSpecCard result={result} />;
    case "finalize_design":
      return <FinalizeDesignCard result={result} />;
    case "share_to_gallery":
      return <GalleryShareCard result={result} />;
    default:
      return null;
  }
}

marked.setOptions({ breaks: true, gfm: true });

function CaraMarkdown({ content }: { content: string }) {
  const html = useMemo(() => {
    try {
      return marked.parse(content, { async: false }) as string;
    } catch {
      return content;
    }
  }, [content]);

  return (
    <div
      className="cara-prose"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

// ── Suggestion Parsing ───────────────────────────────────────────────────────

const SUGGESTIONS_RE = /<suggestions>\s*([\s\S]*?)\s*<\/suggestions>\s*$/;
const OPTION_XML_RE = /<option(?:\s+custom\s*\/?\s*)?\/?>(.*?)<\/option>|<option\s+custom\s*\/?\s*>/g;
const OPTION_LINE_RE = /^-\s+(.+)$/;

export type ParsedSuggestions = {
  options: string[];
  allowCustom: boolean;
};

function parseOptionsFromBlock(block: string): ParsedSuggestions | null {
  const options: string[] = [];
  let allowCustom = false;

  // Try XML <option> tags first
  const xmlMatches = [...block.matchAll(OPTION_XML_RE)];
  if (xmlMatches.length > 0) {
    for (const m of xmlMatches) {
      const isCustomTag = m[0].includes("custom");
      const val = m[1]?.trim();
      if (isCustomTag && (!val || val === "[custom]")) {
        allowCustom = true;
      } else if (val) {
        options.push(val);
      }
    }
    if (options.length > 0) return { options, allowCustom };
  }

  // Fallback: legacy `- Option` list lines
  for (const line of block.split("\n")) {
    const m = line.trim().match(OPTION_LINE_RE);
    if (!m) continue;
    const val = m[1]!.trim();
    if (val.toLowerCase() === "[custom]") {
      allowCustom = true;
    } else {
      options.push(val);
    }
  }

  if (options.length === 0) return null;
  return { options, allowCustom };
}

// Fallback: detect trailing `- Option A - Option B - ...` pattern (no XML tags).
// Matches a block of dash-separated options at the very end of the text,
// preceded by a line that looks like a lead-in question.
const TRAILING_OPTIONS_RE = /\n([^\n]*\?\s*)\n((?:\s*-\s+[^\n]+(?:\s+-\s+[^\n]+)*))$/;

function parseFallbackOptions(text: string): { cleanText: string; suggestions: ParsedSuggestions | null } {
  const match = text.match(TRAILING_OPTIONS_RE);
  if (!match) return { cleanText: text, suggestions: null };

  const items = match[2]!.split(/\s+-\s+/).map((s) => s.replace(/^-\s+/, "").trim()).filter(Boolean);
  let allowCustom = false;
  const options: string[] = [];
  for (const item of items) {
    if (item.toLowerCase() === "[custom]") allowCustom = true;
    else if (item.length <= 40) options.push(item);
  }

  if (options.length < 2) return { cleanText: text, suggestions: null };
  const cleanText = text.slice(0, match.index).trimEnd();
  return { cleanText, suggestions: { options, allowCustom } };
}

export function parseSuggestions(text: string): { cleanText: string; suggestions: ParsedSuggestions | null } {
  const match = text.match(SUGGESTIONS_RE);
  if (match) {
    const cleanText = text.slice(0, match.index).trimEnd();
    const suggestions = parseOptionsFromBlock(match[1]!);
    if (suggestions) return { cleanText, suggestions };
  }

  return parseFallbackOptions(text);
}

// ── Main Component ───────────────────────────────────────────────────────────

import type { UIMessage } from "ai";

type ToolPart = {
  type: string;
  toolCallId?: string;
  toolName?: string;
  state?: string;
  output?: unknown;
  [key: string]: unknown;
};

function isToolPart(part: { type: string }): part is ToolPart {
  return part.type.startsWith("tool-") || part.type === "dynamic-tool";
}

function getToolName(part: ToolPart): string {
  if (part.toolName) return part.toolName;
  if (part.type.startsWith("tool-")) return part.type.slice(5);
  return "unknown";
}

function getToolLoadingMessage(toolName: string, tl: Record<string, string>): string {
  const renderTools = ["generate_mood_board", "generate_design_render", "generate_catalog_render", "adjust_design", "render_from_sketch"];
  if (renderTools.includes(toolName)) return tl.rendering;
  if (toolName === "generate_concept_sketch") return tl.sketchingConcept;
  if (toolName === "analyze_sketch") return tl.analyzingSketch;
  if (toolName === "analyze_inspiration") return tl.analyzingInspiration;
  if (toolName === "reference_lookup") return tl.lookingUpReference;
  if (toolName === "generate_turntable_video") return tl.generatingVideo;
  if (toolName === "generate_lookbook_image") return tl.stylingLookbook;
  if (toolName === "structural_analysis") return tl.structuralAnalysis;
  if (toolName === "material_recommendation") return tl.materialCheck;
  if (toolName === "calculate_bom") return tl.calculatingBom;
  if (toolName === "share_to_gallery") return tl.sharingToGallery;
  if (toolName === "present_design_specs") return tl.preparingSpecs;
  if (toolName === "finalize_design") return tl.finalizingDesign;
  return tl.processing;
}

export function CaraMessageBubble({
  message,
  index,
  isStreaming,
  sessionId,
  sketchDataUrls,
  mode = "designer",
  onEdit,
}: {
  message: UIMessage;
  index: number;
  isStreaming?: boolean;
  sessionId?: string | null;
  sketchDataUrls?: string[];
  mode?: "designer" | "buyer";
  onEdit?: (index: number, newText: string) => void;
}) {
  const { t, locale } = useT();
  const c = t.caraChat;
  const isUser = message.role === "user";
  const delay = Math.min(index * 30, 150);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState("");
  const editRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditing && editRef.current) {
      editRef.current.focus();
      editRef.current.style.height = "auto";
      editRef.current.style.height = `${editRef.current.scrollHeight}px`;
    }
  }, [isEditing]);

  const handleStartEdit = useCallback(() => {
    const textPart = message.parts.find((p) => p.type === "text" && "text" in p);
    const text = textPart ? (textPart as { type: "text"; text: string }).text : "";
    setEditText(text);
    setIsEditing(true);
  }, [message.parts]);

  const handleSubmitEdit = useCallback(() => {
    const trimmed = editText.trim();
    if (!trimmed || !onEdit) return;
    setIsEditing(false);
    onEdit(index, trimmed);
  }, [editText, onEdit, index]);

  const getPlainText = useCallback(() => {
    return message.parts
      .filter((p) => p.type === "text" && "text" in p)
      .map((p) => {
        const raw = (p as { type: "text"; text: string }).text;
        const { cleanText } = parseSuggestions(raw);
        return cleanText;
      })
      .join("\n");
  }, [message.parts]);

  return (
    <div
      data-message-index={index}
      className={`cara-msg-enter flex ${isUser ? "justify-end" : "justify-start"}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div
        className={`group relative ${
          isUser
            ? "max-w-[85%] bg-foreground/[0.04] rounded-2xl rounded-br-sm px-4 py-3"
            : "max-w-[90%] sm:max-w-[85%] pl-8 sm:pl-9 py-1"
        }`}
      >
        {/* Cara avatar indicator for assistant messages */}
        {!isUser && (
          <div className="absolute left-0 top-1.5 h-5 w-5 sm:h-6 sm:w-6 rounded-full flex items-center justify-center" style={{ background: "oklch(0.45 0.08 213 / 0.1)" }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-2.5 w-2.5 sm:h-3 sm:w-3" style={{ color: "var(--teal-sapphire)" }}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z" />
            </svg>
          </div>
        )}

        {/* Action buttons — visible on hover */}
        <div className={`absolute ${isUser ? "left-0 -translate-x-full pr-1.5" : "right-0 translate-x-full pl-1.5"} top-1 opacity-0 group-hover:opacity-100 transition-opacity hidden sm:flex items-center gap-0.5`}>
          {!isUser && !isStreaming && (
            <CopyButton getText={getPlainText} />
          )}
          {isUser && !isStreaming && onEdit && (
            <button
              type="button"
              onClick={handleStartEdit}
              className="h-6 w-6 flex items-center justify-center rounded text-muted-foreground/50 hover:text-foreground hover:bg-muted transition-colors"
              aria-label={c.editMessage}
            >
              <svg viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5">
                <path d="M11.013 1.427a1.75 1.75 0 0 1 2.474 0l1.086 1.086a1.75 1.75 0 0 1 0 2.474l-8.61 8.61c-.21.21-.47.364-.756.445l-3.251.93a.75.75 0 0 1-.927-.928l.929-3.25c.081-.286.235-.547.445-.758l8.61-8.61Z" />
              </svg>
            </button>
          )}
        </div>

        <div className={`text-sm leading-relaxed ${isUser ? "text-foreground/90" : "text-foreground/85"}`}>
          {isEditing ? (
            <div className="space-y-2">
              <textarea
                ref={editRef}
                value={editText}
                onChange={(e) => {
                  setEditText(e.target.value);
                  e.target.style.height = "auto";
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmitEdit(); }
                  if (e.key === "Escape") setIsEditing(false);
                }}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground resize-none focus:outline-none focus:ring-1 focus:ring-teal/40"
              />
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={handleSubmitEdit} disabled={!editText.trim()} className="text-xs h-7 px-3">
                  {c.editSend}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setIsEditing(false)} className="text-xs h-7 px-2">
                  {c.editCancel}
                </Button>
              </div>
            </div>
          ) : (
            message.parts.map((part, i) => {
              if (part.type === "text" && "text" in part) {
                const rawText = (part as { type: "text"; text: string }).text;
                let lastTextIdx = -1;
                for (let j = message.parts.length - 1; j >= 0; j--) {
                  if (message.parts[j]!.type === "text") { lastTextIdx = j; break; }
                }
                const isLastTextPart = !isUser && i === lastTextIdx;
                const { cleanText } = isLastTextPart && !isStreaming
                  ? parseSuggestions(rawText)
                  : { cleanText: rawText };
                return (
                  <div key={i}>
                    <CaraMarkdown content={cleanText} />
                    {isStreaming && i === message.parts.length - 1 && !isUser && (
                      <span className="inline-block w-0.5 h-4 bg-primary animate-pulse ml-0.5 align-text-bottom" />
                    )}
                  </div>
                );
              }
              if (part.type === "file" && "url" in part) {
                const filePart = part as { type: "file"; url: string; mediaType?: string };
                if (filePart.mediaType?.startsWith("image/") || filePart.url.startsWith("data:image/")) {
                  return (
                    <div key={i} className="mt-1 mb-1">
                      <Dialog>
                        <DialogTrigger asChild>
                          <button className="rounded-lg overflow-hidden border border-border/50 hover:scale-[1.02] transition-transform duration-200 cursor-zoom-in">
                            <img src={filePart.url} alt="Uploaded sketch or reference" className="max-w-[140px] sm:max-w-[200px] max-h-[200px] object-cover" loading="lazy" />
                          </button>
                        </DialogTrigger>
                        <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 overflow-hidden">
                          <img src={filePart.url} alt="Uploaded image — full size" className="w-full h-full object-contain" />
                        </DialogContent>
                      </Dialog>
                    </div>
                  );
                }
              }
              if (isToolPart(part as ToolPart)) {
                const tp = part as ToolPart;
                const toolName = getToolName(tp);
                const hasOutput = (tp.state === "result" || tp.state === "output-available") && tp.output;
                if (hasOutput) {
                  return <ToolResultCard key={i} toolName={toolName} result={tp.output as ToolResult} sessionId={sessionId} sketchDataUrls={sketchDataUrls} mode={mode} />;
                }
                if (!isStreaming) return null;
                return (
                  <div key={i} className="mt-3 cara-tool-loading">
                    <div className="rounded-xl border border-border/60 bg-card/50 p-4 max-w-xs sm:max-w-sm">
                      <div className="flex items-center gap-2.5 mb-3">
                        <span className="h-2 w-2 rounded-full shrink-0 animate-pulse" style={{ background: "var(--teal-sapphire)" }} />
                        <span className="text-xs text-muted-foreground font-medium">
                          {getToolLoadingMessage(toolName, c.toolLoading)}
                        </span>
                      </div>
                      <div className="space-y-2">
                        <div className="h-2.5 rounded-full cara-shimmer w-3/4" />
                        <div className="h-2.5 rounded-full cara-shimmer w-1/2" style={{ animationDelay: "150ms" }} />
                        <div className="h-24 rounded-lg cara-shimmer mt-1" style={{ animationDelay: "300ms" }} />
                      </div>
                    </div>
                  </div>
                );
              }
              return null;
            })
          )}
        </div>

        {/* Timestamp on hover */}
        <div className={`opacity-0 group-hover:opacity-100 transition-opacity mt-1.5 flex items-center gap-2 ${isUser ? "" : "ml-0"}`}>
          {"createdAt" in message && (message as { createdAt?: Date | string }).createdAt && (
            <span className="text-[10px] text-muted-foreground/40">
              {formatRelativeTime((message as { createdAt?: Date | string }).createdAt, { justNow: c.justNow, minutesAgo: c.minutesAgo }, locale)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
